// ── BCHNostr — Podcast feed resolver ──────────────────────────────
//
// Creators should NOT have to know what an RSS feed is. They paste a link
// to their show — an Apple Podcasts page, a Spotify link, an Amazon Music
// link, a podcast site, or a raw feed — and we discover the underlying RSS
// feed from it. The RSS feed remains the single source of truth for
// episode metadata (see lib/podcast.ts); a directory URL is only ever an
// INPUT to discovery, never stored as the feed.
//
//   PodcastResolver
//   ├── RSSResolver            the pasted URL already IS a feed
//   ├── ApplePodcastsResolver  itunes.apple.com/lookup?id=… → feedUrl (JSON API)
//   ├── SpotifyResolver        best-effort page scan (Spotify hides feeds)
//   ├── AmazonMusicResolver    best-effort page scan
//   └── GenericPodcastResolver <link rel=alternate type=application/rss+xml>
//
// SECURITY: every external URL and every byte of fetched HTML/JSON is
// UNTRUSTED. We never execute it, never innerHTML it, and always re-validate
// any discovered URL through safeFeedUrl before it leaves this module. We
// never fabricate a feed URL: if discovery can't find a real one, the caller
// falls back to manual entry.

import {
  fetchFeed, safeFeedUrl, safeUrl,
  type PodcastFeed,
} from './podcast';

// ── Result shape ──────────────────────────────────────────────────

/** Which provider handled (or attempted) the resolution. */
export type PodcastPlatform =
  | 'rss' | 'apple' | 'spotify' | 'amazon' | 'generic';

/** Why discovery failed, so the UI can pick the right message + fallback. */
export type ResolveErrorKind =
  | 'invalid-url'      // not a parseable http(s) URL at all
  | 'not-found'        // the platform says this show doesn't exist
  | 'no-feed'          // platform recognised, but no RSS feed could be found
  | 'blocked'          // the platform blocks automated discovery
  | 'not-a-feed'       // a feed URL was found/pasted but it isn't valid RSS
  | 'unreachable';     // network / proxy failure

export interface ResolveResult {
  ok: boolean;
  /** The discovered (or pasted) RSS feed URL — always https, always validated. */
  feedUrl: string;
  /** The parsed & sanitized feed, when discovery reached validation. */
  feed: PodcastFeed | null;
  /** Which resolver produced this result. */
  platform: PodcastPlatform;
  /** Populated only when ok === false. */
  error: ResolveErrorKind | null;
  /** Human-readable message suitable for display. Never leaks internals. */
  message: string;
  /** Publisher/author label surfaced on the confirmation screen, if known. */
  publisher: string;
}

const ERROR_MESSAGES: Record<ResolveErrorKind, string> = {
  'invalid-url': 'That doesn’t look like a valid link. Paste a podcast URL or an RSS feed.',
  'not-found': 'Podcast not found.',
  'no-feed': 'Podcast found, but we couldn’t automatically locate its RSS feed.',
  'blocked': 'Automatic discovery isn’t available for this podcast. Please enter the RSS feed manually.',
  'not-a-feed': 'This feed doesn’t appear to be a valid podcast RSS feed.',
  'unreachable': 'We couldn’t automatically find the RSS feed.',
};

function fail(platform: PodcastPlatform, error: ResolveErrorKind, message?: string): ResolveResult {
  return {
    ok: false,
    feedUrl: '',
    feed: null,
    platform,
    error,
    message: message || ERROR_MESSAGES[error],
    publisher: '',
  };
}

// ── Shared fetch through the read-only proxy ──────────────────────
//
// Directory pages and lookup APIs rarely send CORS headers, so the browser
// can't read them directly. We reuse the same read-only proxy the feed
// fetcher uses. It adds nothing to the request — no credentials, no user
// data — and we only ever fetch small text/JSON documents here, never audio.

const PROXY = 'https://proxy.shakespeare.diy/?url=';
const DISCOVERY_TIMEOUT_MS = 15000;
/** Cap discovery payloads — a directory page shouldn't be megabytes. */
const MAX_DISCOVERY_BYTES = 4 * 1024 * 1024;

function proxied(url: string): string {
  return `${PROXY}${encodeURIComponent(url)}`;
}

/** Fetch text through the proxy. Returns null on any failure — never throws. */
async function fetchText(url: string): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), DISCOVERY_TIMEOUT_MS);
  try {
    const res = await fetch(proxied(url), { signal: controller.signal });
    if (!res.ok) return null;
    const text = await res.text();
    if (text.length > MAX_DISCOVERY_BYTES) return null;
    return text;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// ── Provider abstraction ──────────────────────────────────────────
//
// Each resolver decides whether it recognises a URL (`match`) and, if so,
// tries to produce a feed URL (`discover`). Adding a new platform later is a
// matter of writing one more resolver and dropping it into RESOLVERS — the
// rest of the podcast system is untouched.

interface Resolver {
  platform: PodcastPlatform;
  /** Does this resolver handle the given parsed URL? */
  match(url: URL): boolean;
  /**
   * Attempt to discover the feed URL for this show. Returns the feed URL
   * (https, unvalidated-as-RSS) plus an optional publisher label, or null
   * when the platform is recognised but no feed could be found.
   */
  discover(url: URL): Promise<{ feedUrl: string; publisher?: string } | null>;
}

// ── RSS: the pasted URL already is a feed ─────────────────────────

const RSSResolver: Resolver = {
  platform: 'rss',
  match(url) {
    const path = url.pathname.toLowerCase();
    // Heuristic: obvious feed URLs end in .xml/.rss, or carry feed/rss in the
    // path or hostname. Everything else falls through to a directory resolver
    // or the generic HTML scanner.
    return (
      /\.(xml|rss)$/.test(path) ||
      /(?:^|\/)(?:feed|rss|podcast)s?(?:\/|$)/.test(path) ||
      /(?:^|\.)feeds?\./.test(url.hostname) ||
      url.searchParams.has('feed')
    );
  },
  async discover(url) {
    // Nothing to look up — the URL itself is the candidate feed.
    return { feedUrl: url.toString() };
  },
};

// ── Apple Podcasts: iTunes Lookup API (public JSON, no scraping) ───
//
// Apple exposes a stable public lookup API keyed by the numeric show id in
// the page URL, e.g. …/podcast/gp-spaces/id1797937833 → id 1797937833.
// The API returns JSON with the real `feedUrl`, so we never scrape the HTML
// page and never treat the Apple URL itself as the feed.

/** Pull the numeric Apple show id out of a podcasts.apple.com URL. */
export function appleShowId(url: URL): string {
  // Canonical form: /…/id1234567890
  const fromPath = url.pathname.match(/\/id(\d{3,})/);
  if (fromPath) return fromPath[1];
  // Some share links carry ?i= (an episode id) — the show id is still in
  // the path, but as a fallback accept a bare numeric 'id' query param.
  const q = url.searchParams.get('id');
  if (q && /^\d{3,}$/.test(q)) return q;
  return '';
}

const ApplePodcastsResolver: Resolver = {
  platform: 'apple',
  match(url) {
    return /(?:^|\.)apple\.com$/.test(url.hostname) && url.pathname.includes('/podcast');
  },
  async discover(url) {
    const id = appleShowId(url);
    if (!id) return null;

    // Country segment (…/us/…) tweaks the lookup store but the feedUrl is
    // the same; we let Apple default it.
    const lookup = `https://itunes.apple.com/lookup?id=${encodeURIComponent(id)}&entity=podcast`;
    const text = await fetchText(lookup);
    if (!text) return null;

    let data: { resultCount?: number; results?: Array<Record<string, unknown>> };
    try {
      data = JSON.parse(text);
    } catch {
      return null;
    }

    const result = Array.isArray(data.results) ? data.results[0] : undefined;
    if (!result) return null;

    const feedUrl = typeof result.feedUrl === 'string' ? result.feedUrl : '';
    if (!feedUrl) return null;

    const publisher =
      typeof result.artistName === 'string' ? result.artistName : '';
    return { feedUrl, publisher };
  },
};

// ── Generic HTML: <link rel="alternate" type="application/rss+xml"> ─
//
// A podcast's own website (and many directory pages) advertises its feed in
// the document head. We fetch the HTML and scan for a feed link WITHOUT ever
// parsing it as live DOM in the page — the discovered href is re-validated
// through safeFeedUrl before use.

/**
 * Scan an HTML document for a podcast feed link. Returns the first https
 * candidate, resolved against the page URL. Pure string work — the HTML is
 * never inserted into the live document.
 */
export function findFeedLinkInHtml(html: string, baseUrl: string): string {
  // <link ... type="application/rss+xml" ... href="…"> in any attribute order.
  const linkTags = html.match(/<link\b[^>]*>/gi) || [];
  const candidates: string[] = [];
  for (const tag of linkTags) {
    const type = /type\s*=\s*["']([^"']+)["']/i.exec(tag)?.[1]?.toLowerCase() || '';
    if (type !== 'application/rss+xml' && type !== 'application/atom+xml') continue;
    const href = /href\s*=\s*["']([^"']+)["']/i.exec(tag)?.[1];
    if (href) candidates.push(href);
  }

  for (const raw of candidates) {
    let abs = raw;
    try {
      // Resolve relative hrefs against the page URL.
      abs = new URL(raw, baseUrl).toString();
    } catch {
      continue;
    }
    const safe = safeFeedUrl(abs);
    if (safe) return safe;
  }
  return '';
}

const GenericPodcastResolver: Resolver = {
  platform: 'generic',
  match() {
    // The fallback resolver — it matches anything the specific ones didn't.
    return true;
  },
  async discover(url) {
    const html = await fetchText(url.toString());
    if (!html) return null;
    const feedUrl = findFeedLinkInHtml(html, url.toString());
    if (!feedUrl) return null;
    return { feedUrl };
  },
};

// ── Spotify & Amazon Music: best-effort, honest about limits ──────
//
// Spotify and Amazon Music host their own copies of shows and deliberately
// do NOT expose the underlying RSS feed. There is no public API that maps a
// Spotify/Amazon show URL back to its origin feed. We make a best effort to
// find a feed link in the page (some shows link back to their own site), but
// when that fails we return 'blocked' so the UI cleanly offers manual entry
// rather than inventing a feed URL.

const SpotifyResolver: Resolver = {
  platform: 'spotify',
  match(url) {
    return /(?:^|\.)spotify\.com$/.test(url.hostname);
  },
  async discover(url) {
    const html = await fetchText(url.toString());
    if (html) {
      const feedUrl = findFeedLinkInHtml(html, url.toString());
      if (feedUrl) return { feedUrl };
    }
    // No reliable public mapping from a Spotify show to its origin feed.
    return null;
  },
};

const AmazonMusicResolver: Resolver = {
  platform: 'amazon',
  match(url) {
    return (
      /(?:^|\.)music\.amazon\.[a-z.]+$/.test(url.hostname) ||
      (/(?:^|\.)amazon\.[a-z.]+$/.test(url.hostname) && url.pathname.includes('/podcast'))
    );
  },
  async discover(url) {
    const html = await fetchText(url.toString());
    if (html) {
      const feedUrl = findFeedLinkInHtml(html, url.toString());
      if (feedUrl) return { feedUrl };
    }
    return null;
  },
};

// Order matters: specific directory resolvers first, then the RSS heuristic,
// then the generic HTML scanner as the catch-all.
const RESOLVERS: Resolver[] = [
  ApplePodcastsResolver,
  SpotifyResolver,
  AmazonMusicResolver,
  RSSResolver,
  GenericPodcastResolver,
];

/** Which provider would handle this URL (before any network call). */
export function detectPlatform(rawUrl: string): PodcastPlatform | null {
  const parsed = parseHttpUrl(rawUrl);
  if (!parsed) return null;
  for (const resolver of RESOLVERS) {
    if (resolver.match(parsed)) return resolver.platform;
  }
  return null;
}

/** Platforms where a failed discovery means "you must enter RSS manually". */
function blocksDiscovery(platform: PodcastPlatform): boolean {
  return platform === 'spotify' || platform === 'amazon';
}

// ── URL parsing ───────────────────────────────────────────────────

/**
 * Parse user input into an http(s) URL, tolerating a bare hostname the way
 * the rest of the app does. Returns null for anything that isn't a web URL.
 */
function parseHttpUrl(raw: string): URL | null {
  const trimmed = (raw || '').trim();
  if (!trimmed) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (url.username || url.password) return null;
    return url;
  } catch {
    return null;
  }
}

// ── Public entry point ────────────────────────────────────────────

/**
 * Resolve a pasted podcast URL to a validated RSS feed.
 *
 * The full pipeline the addendum describes:
 *   1. identify the platform,
 *   2. extract the show identifier where possible,
 *   3. discover the origin RSS feed,
 *   4. validate the feed and pull metadata,
 *   5. return a typed result the confirmation screen can render.
 *
 * Never throws, never fabricates a feed URL. When discovery can't reach a
 * valid feed the result carries a typed error and the caller offers manual
 * RSS entry.
 */
export async function resolvePodcast(rawUrl: string): Promise<ResolveResult> {
  const parsed = parseHttpUrl(rawUrl);
  if (!parsed) return fail('generic', 'invalid-url');

  // 1 + 2: find the resolver that recognises this URL.
  const resolver = RESOLVERS.find(r => r.match(parsed)) || GenericPodcastResolver;

  // 3: discover the candidate feed URL.
  let discovered: { feedUrl: string; publisher?: string } | null = null;
  try {
    discovered = await resolver.discover(parsed);
  } catch {
    discovered = null;
  }

  if (!discovered || !discovered.feedUrl) {
    // Recognised platform, no feed found. Spotify/Amazon can't be resolved
    // automatically, so we say so plainly and let the UI offer manual entry.
    if (blocksDiscovery(resolver.platform)) {
      return fail(resolver.platform, 'blocked');
    }
    // Apple lookup returned nothing usable → the show wasn't found there.
    if (resolver.platform === 'apple') {
      return fail('apple', 'not-found');
    }
    return fail(resolver.platform, 'no-feed');
  }

  // Re-validate the discovered URL before we ever fetch or store it.
  const feedUrl = safeFeedUrl(discovered.feedUrl);
  if (!feedUrl) return fail(resolver.platform, 'no-feed');

  // 4: validate the feed and pull sanitized metadata.
  const { feed, message } = await fetchFeed(feedUrl);
  if (!feed) {
    // A discovered-but-broken feed is a different failure from "no feed":
    // for a raw RSS paste this is the "not a valid RSS feed" case; for a
    // directory URL the discovered feed is simply unreachable/invalid.
    if (resolver.platform === 'rss') {
      return fail('rss', 'not-a-feed', message || ERROR_MESSAGES['not-a-feed']);
    }
    return fail(resolver.platform, 'unreachable');
  }

  // 5: success. Prefer the platform-supplied publisher, else the feed author.
  return {
    ok: true,
    feedUrl,
    feed,
    platform: resolver.platform,
    error: null,
    message: '',
    publisher: (discovered.publisher || feed.author || '').trim(),
  };
}

/** Human label for a platform, for the confirmation screen. */
export function platformLabel(platform: PodcastPlatform): string {
  switch (platform) {
    case 'apple': return 'Apple Podcasts';
    case 'spotify': return 'Spotify';
    case 'amazon': return 'Amazon Music';
    case 'rss': return 'RSS Feed';
    case 'generic': return 'Podcast Website';
  }
}

/** Re-export for callers that need the loose URL check. */
export { safeUrl };
